import { useEffect, useRef } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * useLiveEvents connects to /api/ws and fires onEvent when server pushes updates.
 * Params:
 *   - tracking (optional): join tracking room for public rastreo
 *   - onEvent: callback (event) => void
 * Automatically uses localStorage token if available.
 */
export function useLiveEvents({ tracking, onEvent, enabled = true } = {}) {
  const wsRef = useRef(null);
  const retryRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    if (!BACKEND) return;
    let closed = false;
    let timeout;

    const connect = () => {
      const token = localStorage.getItem("voycargo_token");
      // wss:// if https backend
      const proto = BACKEND.startsWith("https") ? "wss" : "ws";
      const host = BACKEND.replace(/^https?:\/\//, "");
      const params = new URLSearchParams();
      if (token) params.set("token", token);
      if (tracking) params.set("tracking", tracking);
      if (!token && !tracking) return; // nothing to subscribe to
      const url = `${proto}://${host}/api/ws?${params.toString()}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => { retryRef.current = 0; };
      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (onEvent) onEvent(data);
        } catch (e) { void e; }
      };
      ws.onclose = () => {
        if (closed) return;
        // retry with backoff
        const wait = Math.min(30000, 1000 * Math.pow(2, retryRef.current++));
        timeout = setTimeout(connect, wait);
      };
      ws.onerror = () => { try { ws.close(); } catch (e) { void e; } };
    };

    connect();
    return () => {
      closed = true;
      if (timeout) clearTimeout(timeout);
      if (wsRef.current) { try { wsRef.current.close(); } catch (e) { void e; } }
    };
  }, [tracking, enabled]);
}
